import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Popover, List, ListItem, Typography, TextField, InputAdornment, Button } from '@mui/material';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { useLazyFetchWeeklyGLPostingSublocationsQuery } from '../../../services/WeeklyGLPosting/WeeklyGLPostingService';
import ImmediateCloseTooltip from './ImmediateCloseTooltip';
import useInfiniteScrollItems from '../hooks/useInfiniteScrollItems';
import useResettableStateSetter from '../hooks/useResettableStateSetter';
import { sortSublocationsByName } from '../utils/sortSublocations';

const useStyles = makeStyles((theme) => ({
  trigger: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    textAlign: 'inherit',
    '&:hover': {
      color: 'inherit',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.grey[400]}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  triggerIcon: {
    fontSize: '18px',
    color: 'inherit',
  },
  singleValue: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  paper: {
    maxHeight: 380,
    minWidth: 300,
    maxWidth: 400,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.customShadows?.popover,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  headerTitle: {
    fontWeight: theme.typography.fontWeightSemiBold,
    color: theme.palette.text.primary,
    fontSize: '14px',
  },
  tabsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tabButton: {
    flex: '1 1 0',
    minWidth: `calc(50% - ${theme.spacing(0.5)})`,
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    fontSize: '12px',
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
  },
  row: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  rowMain: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
  },
  rowName: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowDate: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1),
  },
  emptyText: {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

const normalizeSublocationEntry = (sublocation) => {
  if (!sublocation) return null;

  if (typeof sublocation === 'string') {
    const name = sublocation.trim();
    if (!name) return null;
    return { id: '', name, endDate: '' };
  }

  if (typeof sublocation !== 'object') return null;

  const name = String(sublocation?.name ?? '').trim();
  if (!name) return null;

  return {
    id: String(sublocation?.id ?? sublocation?.sublocationId ?? '').trim(),
    name,
    endDate: String(sublocation?.endDate ?? sublocation?.lastPosting?.endDate ?? '').trim(),
  };
};

const normalizeSublocationList = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const unique = new Map();
  items.forEach((item) => {
    const normalized = normalizeSublocationEntry(item);
    if (!normalized) return;
    const key = `${normalized.id}|${normalized.name.toLowerCase()}`;
    if (unique.has(key)) return;
    unique.set(key, normalized);
  });

  return Array.from(unique.values());
};

const SublocationsPopoverCell = ({
  sublocations = [],
  selectedSublocations,
  unselectedSublocations,
  valueVariant = 'table',
  x3ProductName = '',
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('selected');
  const [derivedUnselectedItems, setDerivedUnselectedItems] = useState([]);
  const [isDerivedUnselectedResolved, setIsDerivedUnselectedResolved] = useState(false);
  const [fetchSublocations] = useLazyFetchWeeklyGLPostingSublocationsQuery();

  const isOpen = Boolean(anchorEl);
  const isDetailsVariant = valueVariant === 'details';

  const resolvedSelectedItems = useMemo(() => {
    const source =
      Array.isArray(selectedSublocations) && selectedSublocations.length > 0 ? selectedSublocations : sublocations;
    return normalizeSublocationList(source);
  }, [selectedSublocations, sublocations]);

  const resolvedUnselectedItems = useMemo(() => {
    const selectedKeys = new Set(
      resolvedSelectedItems.map(
        (item) =>
          `${String(item?.id ?? '').trim()}|${String(item?.name ?? '')
            .trim()
            .toLowerCase()}`,
      ),
    );

    return normalizeSublocationList(unselectedSublocations).filter((item) => {
      const key = `${String(item?.id ?? '').trim()}|${String(item?.name ?? '')
        .trim()
        .toLowerCase()}`;
      return !selectedKeys.has(key);
    });
  }, [unselectedSublocations, resolvedSelectedItems]);

  const selectedCount = resolvedSelectedItems.length;
  const isTabbedReadOnlyView = Array.isArray(selectedSublocations) || Array.isArray(unselectedSublocations);
  const shouldDeriveUnselected = isTabbedReadOnlyView && resolvedUnselectedItems.length === 0 && Boolean(x3ProductName);
  const effectiveUnselectedItems = shouldDeriveUnselected ? derivedUnselectedItems : resolvedUnselectedItems;
  const unselectedCount = effectiveUnselectedItems.length;
  const totalCount = selectedCount + unselectedCount;

  const itemsForActiveTab = useMemo(
    () => sortSublocationsByName(activeTab === 'selected' ? resolvedSelectedItems : effectiveUnselectedItems),
    [activeTab, resolvedSelectedItems, effectiveUnselectedItems],
  );
  const filteredItems = useMemo(() => {
    const normalizedSearch = String(searchTerm ?? '')
      .trim()
      .toLowerCase();
    if (!normalizedSearch) return itemsForActiveTab;
    return itemsForActiveTab.filter((item) => item.name.toLowerCase().includes(normalizedSearch));
  }, [itemsForActiveTab, searchTerm]);
  const {
    visibleItemsCount,
    resetVisibleItems,
    handleScroll: handlePopoverScroll,
  } = useInfiniteScrollItems({
    totalItems: filteredItems.length,
  });
  const visibleFilteredItems = useMemo(
    () => filteredItems.slice(0, visibleItemsCount),
    [filteredItems, visibleItemsCount],
  );

  useEffect(() => {
    if (!isOpen) return;
    resetVisibleItems();
  }, [isOpen, activeTab, searchTerm, resetVisibleItems]);

  const valueTextStyle = useMemo(
    () =>
      isDetailsVariant
        ? {
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            lineHeight: '24px',
            minHeight: 24,
          }
        : {
            color: 'inherit',
            font: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
          },
    [isDetailsVariant, theme],
  );

  const handleOpen = useCallback(
    (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setActiveTab('selected');
      setSearchTerm('');
      resetVisibleItems();
    },
    [resetVisibleItems],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchTerm('');
  }, []);

  const handleTabChange = useResettableStateSetter(setActiveTab, resetVisibleItems);

  const selectedKeySet = useMemo(
    () =>
      new Set(
        resolvedSelectedItems.map(
          (item) =>
            `${String(item?.id ?? '').trim()}|${String(item?.name ?? '')
              .trim()
              .toLowerCase()}`,
        ),
      ),
    [resolvedSelectedItems],
  );

  useEffect(() => {
    if (!shouldDeriveUnselected) {
      setIsDerivedUnselectedResolved(false);
      return undefined;
    }

    let isActive = true;

    const loadUnselectedSublocations = async () => {
      setIsDerivedUnselectedResolved(false);
      try {
        const response = await fetchSublocations({ x3ProductName }, true).unwrap();
        if (!isActive) return;

        const allSublocations = normalizeSublocationList(response?.sublocations);
        const nextDerivedUnselected = allSublocations.filter((item) => {
          const key = `${String(item?.id ?? '').trim()}|${String(item?.name ?? '')
            .trim()
            .toLowerCase()}`;
          return !selectedKeySet.has(key);
        });
        setDerivedUnselectedItems(nextDerivedUnselected);
        setIsDerivedUnselectedResolved(true);
      } catch (error) {
        if (!isActive) return;
        setDerivedUnselectedItems([]);
        setIsDerivedUnselectedResolved(false);
      }
    };

    loadUnselectedSublocations();

    return () => {
      isActive = false;
    };
  }, [fetchSublocations, selectedKeySet, shouldDeriveUnselected, x3ProductName]);

  if (totalCount === 0) {
    return <Typography className={classes.emptyText}>N/A</Typography>;
  }

  const singleSelectedName = selectedCount === 1 ? resolvedSelectedItems[0]?.name || '' : '';
  const hasStableTotalCount = !shouldDeriveUnselected || isDerivedUnselectedResolved;
  const knownTotalCount = hasStableTotalCount ? totalCount : null;
  const isAllSelected =
    Number.isFinite(knownTotalCount) &&
    knownTotalCount > 0 &&
    unselectedCount === 0 &&
    selectedCount === knownTotalCount;
  const triggerLabel = singleSelectedName
    ? singleSelectedName
    : isAllSelected
    ? `All ${knownTotalCount} selected`
    : `${selectedCount} sublocation${selectedCount === 1 ? '' : 's'}`;

  return (
    <>
      <button type="button" className={classes.trigger} onClick={handleOpen} style={valueTextStyle}>
        <ImmediateCloseTooltip title={singleSelectedName || ''} disableHover={!singleSelectedName}>
          <span className={classes.singleValue}>{triggerLabel}</span>
        </ImmediateCloseTooltip>
        {isOpen ? (
          <ExpandLessIcon className={classes.triggerIcon} />
        ) : (
          <ExpandMoreIcon className={classes.triggerIcon} />
        )}
      </button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.paper }}
        onClick={(event) => event.stopPropagation()}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>Sublocations</Typography>
          <Box className={classes.tabsRow}>
            <Button
              size="small"
              variant={activeTab === 'selected' ? 'contained' : 'outlined'}
              onClick={() => handleTabChange('selected')}
              className={classes.tabButton}
            >
              {`Selected (${selectedCount})`}
            </Button>
            <Button
              size="small"
              variant={activeTab === 'unselected' ? 'contained' : 'outlined'}
              onClick={() => handleTabChange('unselected')}
              className={classes.tabButton}
            >
              {`Unselected (${unselectedCount})`}
            </Button>
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search sublocations..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              resetVisibleItems();
            }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List disablePadding sx={{ py: 0.5 }} className={classes.list} onScroll={handlePopoverScroll}>
          {visibleFilteredItems.length > 0 ? (
            visibleFilteredItems.map((item, index) => (
              <ListItem key={`${item.id}|${item.name}|${index}`} className={classes.row} disableGutters={false}>
                <Box className={classes.rowMain}>
                  <ImmediateCloseTooltip title={item.name}>
                    <Typography className={classes.rowName}>{item.name}</Typography>
                  </ImmediateCloseTooltip>
                  {item.endDate ? <Typography className={classes.rowDate}>{item.endDate}</Typography> : null}
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem className={classes.row} disableGutters={false}>
              <Typography className={classes.emptyText}>
                {searchTerm
                  ? 'No matching sublocations'
                  : activeTab === 'selected'
                  ? 'No selected sublocations'
                  : 'No unselected sublocations'}
              </Typography>
            </ListItem>
          )}
        </List>
      </Popover>
    </>
  );
};

export default SublocationsPopoverCell;
